/**
 * titleLanguage
 *
 * Lightweight detector for "what language is this short thread title
 * in?" — used by the Bug G backfill pass that re-translates
 * auto-generated thread titles when the UI language and the title
 * language disagree (Miles 2026-05-11, thread
 * `C0AM8U804KZ/1778281880.499349`).
 *
 * Scope is intentionally narrow: titles are 1–5 words. We don't need
 * a full NL-detect library; we just need a robust heuristic that maps
 * a title to one of `'en' | 'es' | 'unknown'` so the backfill can
 * decide whether to re-run inference. Conservative on purpose: when in
 * doubt, return `'unknown'` and the backfill skips the thread.
 */

import type { UiLanguage } from './uiLanguage';

export type TitleLanguageGuess = UiLanguage | 'unknown';

/**
 * Localized default titles for fresh threads. We always know these are
 * in the language of their text, so they short-circuit the heuristic.
 */
const DEFAULT_TITLES_EN = new Set([
  'New thread',
  'New Thread',
  'new thread',
]);
const DEFAULT_TITLES_ES = new Set([
  'Nuevo hilo',
  'Nuevo Hilo',
  'nuevo hilo',
]);

/**
 * Spanish-only character class. If a title contains any of these
 * characters, it's almost certainly Spanish.
 */
const SPANISH_DIACRITICS = /[áéíóúüñ¿¡ÁÉÍÓÚÜÑ]/u;

/**
 * Common short Spanish function/content words that show up in 1–5 word
 * titles. Kept small and high-precision so we don't false-positive on
 * English titles that happen to share short words.
 */
const SPANISH_TITLE_WORDS = /\b(de|del|la|el|los|las|para|por|con|en|sobre|al|y|o|un|una|caso|asunto|hilo|nuevo|cliente|abogado|asilo|inmigración|inmigracion|derecho|laboral|consulta|carta|revisión|revision|preguntas|trabajo|trabajador|trabajadora)\b/iu;

/**
 * Common short English function/content words. Same precision-first
 * approach as the Spanish list.
 */
const ENGLISH_TITLE_WORDS = /\b(the|of|and|or|for|with|about|on|in|to|from|new|thread|case|asylum|immigration|workers|comp|employment|client|matter|review|questions|consultation|letter|demand|application|hearing|prep|status)\b/i;

/**
 * Detect the language of a thread title using simple, high-precision
 * heuristics. Returns `'unknown'` whenever the signal is ambiguous so
 * the backfill pass treats the thread as 'leave alone'.
 */
export function detectTitleLanguage(title: string | null | undefined): TitleLanguageGuess {
  if (typeof title !== 'string') return 'unknown';
  const trimmed = title.trim();
  if (trimmed.length === 0) return 'unknown';

  // Default-title fast paths.
  if (DEFAULT_TITLES_EN.has(trimmed)) return 'en';
  if (DEFAULT_TITLES_ES.has(trimmed)) return 'es';

  // Spanish-only diacritics are a near-deterministic signal.
  if (SPANISH_DIACRITICS.test(trimmed)) return 'es';

  const esHits = (trimmed.match(new RegExp(SPANISH_TITLE_WORDS.source, 'giu')) || []).length;
  const enHits = (trimmed.match(new RegExp(ENGLISH_TITLE_WORDS.source, 'gi')) || []).length;

  // Decisive single-side hit.
  if (esHits > 0 && enHits === 0) return 'es';
  if (enHits > 0 && esHits === 0) return 'en';

  // Tie or no hits at all — don't guess. Backfill will skip this
  // thread; the next user message will trigger a fresh inference run
  // through the UI-language path which IS deterministic.
  return 'unknown';
}

export default detectTitleLanguage;
